import React, { type FC, useEffect, useRef, useState, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';
import { Box, Heading, Paper, Text, Container, Image } from '@lucid/components';

import type { Lucid } from 'types/types';
import FlexiCta from '../flexi-cta/flexi-cta';

const FlexiHighlightsSection: FC<PropsWithChildren<Lucid.Directus.Generated.Flexi_Highlights_Section>> = ({
  heading,
  items,
}) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const chosenArticle = items?.[activeSectionIndex];

  const isHighlightClicked = useRef(false);
  const thisHighlight = useRef(null);

  useEffect(() => {
    if (isHighlightClicked.current && window.innerWidth <= 500) {
      const current = thisHighlight.current as unknown as HTMLElement;

      const activeHighlight = current.querySelector('.nineteenHighlightsListItem--isActive');

      if (activeHighlight) {
        activeHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSectionIndex]);

  // Unselect the default active section on tablet and lower
  useEffect(() => {
    if (window.innerWidth <= 900) {
      setActiveSectionIndex(-1);
    }
  }, []);

  const getHighlightContent = (
    article: Lucid.Directus.Generated.Flexi_Highlights_Section_Flexi_Article,
    isMobile: boolean,
  ) => {
    const article_id = article.flexi_article_id;

    return (
      <Paper
        css={theme => [
          {
            marginTop: theme.space.s40,
            padding: `${theme.space.s64}px ${theme.space.s104}px`,
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
            [theme.breakpoints.bp900.max]: {
              padding: `${theme.space.s48}px`,
              flexWrap: 'wrap',
            },
            [theme.breakpoints.bp900.min]: {
              boxShadow: 'none',
              border: 0,
            },
            [theme.breakpoints.bp500.max]: {
              display: 'none',
            },
          },
          isMobile && {
            display: 'none',
            [theme.breakpoints.bp500.max]: {
              ['.nineteen-highlights__list-item--active &']: {
                display: 'block',
                padding: `${theme.space.s0}`,
                marginBottom: theme.space.s32,
                border: 0,
                boxShadow: 'none',
                borderRadius: 0,
                background: 'transparent',
              },
            },
          },
        ]}
        variant="raised"
      >
        <button
          css={theme => ({
            position: 'absolute',
            display: 'none',
            right: theme.space.s16,
            top: theme.space.s16,
            color: theme.colors.coolGrey[100],
            fontSize: '1.5rem',
            background: 'transparent',
            border: 0,
            padding: `${theme.space.s8}px 0.75rem`,
            [theme.breakpoints.bp900.max]: {
              display: 'block',
            },
            [theme.breakpoints.bp500.max]: {
              display: 'none',
            },
            ['&:hover']: {
              cursor: 'pointer',
            },
          })}
          onClick={() => setActiveSectionIndex(-1)}
          type="button"
        >
          ✕
        </button>
        <Box
          css={theme => ({
            flex: '0 1 45%',
            maxWidth: '45%',
            [theme.breakpoints.bp900.max]: {
              flex: '0 1 100%',
              maxWidth: '100%',
            },
            ['.nineteen-cta']: {
              [theme.breakpoints.bp900.max]: {
                marginBottom: theme.space.s16,
              },
            },
          })}
        >
          <Heading
            as="h2"
            css={theme => ({
              [theme.breakpoints.bp500.max]: {
                display: 'none',
              },
            })}
            variant="headingXL"
          >
            {article_id?.heading}
          </Heading>
          {article_id?.text && (
            <Text
              aria-level={3}
              css={theme => ({
                margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s16}px`,
              })}
              role="heading"
              variant="headingM"
            >
              {article_id?.text}
            </Text>
          )}
          <Box
            css={theme => ({
              margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s32}px`,
              [theme.breakpoints.bp500.max]: {
                margin: 0,
              },
            })}
            dangerouslySetInnerHTML={{
              __html: article_id?.html!,
            }}
          />
          {article_id?.links?.map(({ links_id }, i) => <FlexiCta key={`link-${i}`} data={links_id} />)}
        </Box>
        <Box
          css={theme => ({
            flex: '0 1 47%',
            maxWidth: '47%',
            [theme.breakpoints.bp900.max]: {
              flex: '0 1 100%',
              maxWidth: '100%',
            },
            [theme.breakpoints.bp500.max]: {
              display: 'none',
            },
          })}
        >
          <Image imageAlt={article_id?.image?.translations?.[0]?.alt || ''} imageSrc={withImage(article_id?.image)} />
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      ref={thisHighlight}
      as="section"
      css={theme => ({
        label: 'flexiHighlightsSection',
        paddingTop: theme.space.s24,
        paddingBottom: theme.space.s24,
        [theme.breakpoints.bp768.min]: {
          paddingBottom: theme.space.s80,
          paddingTop: theme.space.s80,
        },
      })}
    >
      <Container>
        {heading && (
          <Heading
            as="h2"
            css={theme => ({
              marginBottom: theme.space.s64,
            })}
            variant="headingXL"
          >
            {heading}
          </Heading>
        )}
        <ul
          css={theme => [
            {
              margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s24}px`,
              listStyle: 'none',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              [theme.breakpoints.bp900.max]: {
                margin: theme.space.s0,
                ['&::after']: {
                  content: '""',
                  flex: '0 1 30%',
                },
              },
            },
            activeSectionIndex >= 0 && {
              [theme.breakpoints.bp900.max]: {
                height: 0,
                overflow: 'hidden',
                margin: theme.space.s0,
              },
              [theme.breakpoints.bp500.max]: {
                height: 'auto',
              },
            },
          ]}
        >
          {items?.map((article, i) => {
            const isActive = i === activeSectionIndex;
            const article_id = article.flexi_article_id;

            return (
              <li
                key={`article-${i}`}
                className={`${isActive ? 'nineteenHighlightsListItem--isActive' : ''}`}
                css={theme => [
                  {
                    flex: '0 1 19%',
                    maxWidth: '19%',
                    maxHeight: '100%',
                    alignItems: 'stretch',
                    backgroundColor: theme.colors.coolGrey[5],
                    transform: 'translateY(0)',
                    transition: 'background-color 0.25s ease, transform 0.25s ease-in',
                    boxShadow: theme.shadows.listItem,
                    position: 'relative',
                    [theme.breakpoints.bp900.max]: {
                      flex: '0 1 30%',
                      maxWidth: '30%',
                      margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s32}px`,
                    },
                    [theme.breakpoints.bp500.max]: {
                      flex: '0 1 100%',
                      maxWidth: '100%',
                      backgroundColor: 'transparent',
                    },
                  },
                  isActive && {
                    backgroundColor: theme.colors.indigo[70],
                    transform: 'translateY(10%)',
                    [theme.breakpoints.bp500.max]: {
                      backgroundColor: 'transparent',
                      flex: '0 1 100%',
                      maxWidth: '100%',
                    },
                  },
                ]}
              >
                <button
                  css={theme => ({
                    padding: theme.space.s0,
                    background: 'transparent',
                    textAlign: 'left',
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'stretch',
                    flexDirection: 'column',
                    alignContent: 'flex-start',
                    border: 0,
                    [theme.breakpoints.bp500.max]: {
                      border: `1px solid ${theme.colors.indigo[70]}`,
                      flexDirection: 'row',
                      height: 'auto',
                      alignItems: 'center',
                    },
                    ['&:focus']: {
                      outlineWidth: '2px',
                      outlineColor: theme.colors.indigo[30],
                    },
                    ['&:hover']: {
                      cursor: 'pointer',
                    },
                  })}
                  onClick={() => {
                    if (window.screen.width <= 900 && i === activeSectionIndex) {
                      isHighlightClicked.current = false;
                      setActiveSectionIndex(-1);
                    } else {
                      isHighlightClicked.current = true;
                      setActiveSectionIndex(i);
                    }
                  }}
                  type="button"
                >
                  <Box
                    css={theme => ({
                      flex: '0 1 auto',
                      padding: theme.space.s16,
                      backgroundColor: theme.colors.white,
                      [theme.breakpoints.bp500.max]: {
                        flex: '0 1 15rem',
                        maxWidth: '15rem',
                        padding: '0.75rem',
                      },
                    })}
                  >
                    <Image
                      css={{
                        maxHeight: '150px',
                        width: '100%',
                      }}
                      imageAlt={article_id?.image?.translations?.[0]?.alt || ''}
                      imageSrc={withImage(article_id?.image)}
                    />
                  </Box>
                  <Text
                    css={theme => ({
                      flex: '0 1 auto',
                      padding: theme.space.s16,
                      margin: theme.space.s0,
                      color: isActive ? theme.colors.white : theme.colors.coolGrey[50],
                      position: 'relative',
                      width: '100%',
                      transition: 'background-color 0.25s ease',
                      [theme.breakpoints.bp900.max]: {
                        height: '100%',
                      },
                      [theme.breakpoints.bp500.max]: {
                        flex: '0 1 auto',
                        maxWidth: '100%',
                        padding: `${theme.space.s40}px ${theme.space.s16}px`,
                        color: theme.colors.coolGrey[60],
                      },
                    })}
                    variant="paragraphS400"
                  >
                    {article_id?.heading}
                  </Text>
                </button>
                {chosenArticle && getHighlightContent(chosenArticle, true)}
              </li>
            );
          })}
        </ul>
        {chosenArticle && getHighlightContent(chosenArticle, false)}
      </Container>
    </Box>
  );
};

export default FlexiHighlightsSection;
